import type { CrowdexUIMessagePart } from '@crowdex/agent-contracts';
import { MessageAttachment } from '@crowdex/ui/ai/message';
import { Reasoning, ReasoningContent, ReasoningTrigger } from '@crowdex/ui/ai/reasoning';
import { McpAppFrame } from '@/components/mcp-app-frame';
import { isBackgroundTerminal } from '@/lib/conversations/message-part/terminal';
import { BackgroundTerminalCard } from './background-terminal-card';
import { DiffToolCard } from './diff-tool-card';
import { DynamicTool } from './dynamic-tool';
import { ElementSelectionText } from './element-selection-text';
import { ElicitationCard } from './elicitation-card';
import { DesktopPlanCard } from './plan-card';
import { SessionEventDivider } from './session-event-divider';
import { SubagentView } from './subagent-view';
import { TerminalToolCard } from './terminal-tool-card';

export function DesktopMessagePart({
  isStreaming,
  onOpenSubagent,
  onOpenTerminal,
  onOpenPlan,
  part,
}: {
  isStreaming?: boolean;
  onOpenSubagent?: (toolCallId: string) => void;
  onOpenTerminal?: (terminalId: string) => void;
  onOpenPlan?: () => void;
  part: CrowdexUIMessagePart;
}) {
  switch (part.type) {
    case 'text':
      return <ElementSelectionText isStreaming={isStreaming} text={part.text} />;
    case 'file':
      return <MessageAttachment data={part} />;
    case 'reasoning':
      // Models often finish a thinking block with no visible text (redacted or
      // whitespace-only); a "Thought" row that opens on nothing is just noise.
      if (part.state !== 'streaming' && part.text.trim() === '') {
        return null;
      }
      return (
        <Reasoning className="mb-0 text-muted-foreground" isStreaming={part.state === 'streaming'}>
          <ReasoningTrigger />
          <ReasoningContent className="type-ui leading-5">{part.text}</ReasoningContent>
        </Reasoning>
      );
    case 'dynamic-tool': {
      return <DynamicTool onOpenTerminal={onOpenTerminal} part={part} />;
    }
    case 'data-plan': {
      return <DesktopPlanCard data={part.data} onOpen={onOpenPlan} />;
    }
    case 'data-diff':
      return <DiffToolCard data={part.data} />;
    case 'data-terminal':
      if (isBackgroundTerminal(part.data.output)) {
        return (
          <BackgroundTerminalCard
            data={part.data}
            onOpen={
              onOpenTerminal === undefined ? undefined : () => onOpenTerminal(part.data.terminalId)
            }
          />
        );
      }
      return <TerminalToolCard data={part.data} />;
    case 'data-subagent':
      return (
        <SubagentView data={part.data} onOpen={() => onOpenSubagent?.(part.data.toolCallId)} />
      );
    case 'data-mcpApp':
      return <McpAppFrame data={part.data} />;
    case 'data-sessionEvent':
      return <SessionEventDivider data={part.data} />;
    case 'data-elicitation':
      return <ElicitationCard data={part.data} />;
    default:
      return null;
  }
}
